// order-history/components/shared/order-card/order-card.component.tsx
import dayjs from 'dayjs'
import { ExternalLink, RefreshCcw } from 'lucide-react'
import { memo, useCallback } from 'react'

import { OrderStatusBadgeComponent } from '@/modules/shared/components/order-status-badge.component'
import { Card, CardContent, CardHeader } from '@/modules/shared/components/ui/card'
import { Checkbox } from '@/modules/shared/components/ui/checkbox'
import { Badge } from '@/modules/shared/components/ui/badge'
import { cn } from '@/modules/shared/lib/utils'
import { priceFormatter } from '@/modules/shared/utils/formatter'
import { formatCnpjCpf } from '@/modules/customer/helpers/format-fields-customer-form.helper'

import { useOrderFormStore } from '../../../stores/order-form.store'
import type { Order } from '../../../types/order.type'

export interface OrderCardComponentProps {
	order: Order
	index: number
	isTransmissionPage?: boolean
	isSelected?: boolean
	onSelect?: (order: Order) => void
	onView?: (orderId: number) => void
	onToggleSelection?: (orderId: string) => void
	isInSelectionArray?: boolean
}

export const OrderCardComponent = memo(function OrderCardComponent({
	order,
	index,
	isTransmissionPage = false,
	isSelected = false,
	onSelect,
	onView,
	onToggleSelection,
	isInSelectionArray = false,
}: OrderCardComponentProps) {
	const { orderSelected } = useOrderFormStore()

	const handleCardClick = useCallback(() => {
		if (isTransmissionPage) {
			onToggleSelection?.(String(order.idPedidoCel))
		} else {
			onSelect?.(order)
		}
	}, [isTransmissionPage, onToggleSelection, onSelect, order])

	const handleViewClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation()
		onView?.(order.idPedidoCel)
	}, [onView, order.idPedidoCel])

	const handleToggleSelection = useCallback(() => {
		onToggleSelection?.(String(order.idPedidoCel))
	}, [onToggleSelection, order.idPedidoCel])

	const isCurrentlySelected = isTransmissionPage
		? isInSelectionArray
		: (orderSelected?.idPedidoCel === order?.idPedidoCel &&
			orderSelected?.fkUsuarioCel === order?.fkUsuarioCel &&
			orderSelected.idPedido === order.idPedido)

	return (
		<Card
			className={cn(
				'cursor-pointer',
				'relative h-full',
				'ease-in-out',
				isCurrentlySelected && !isTransmissionPage
					? 'bg-sky-500/30'
					: 'white',
			)}
			onClick={handleCardClick}
		>
			<CardHeader className={cn('p-3 -mb-5 mr-8', isTransmissionPage && 'mr-0')}>
				<div className="flex justify-between items-center text-sm">
					{isTransmissionPage && (
						<Checkbox
							id={index.toString()}
							className="border-blue-500 data-[state=checked]:bg-blue-500"
							checked={isInSelectionArray}
							onCheckedChange={handleToggleSelection}
						/>
					)}
					<p>
						{`Nº: ${order.idPedidoCel}`}
						{order.idPedido !== order.idPedidoCel && `/ ${order.idPedido}`}
					</p>

					<OrderStatusBadgeComponent
						sigla={order.statusSigla}
						status={order.status}
					/>
				</div>
			</CardHeader>

			<CardContent className={cn('p-3 pb-1 mr-8 z-30', isTransmissionPage && 'mr-0')}>
				<div className="text-sm flex flex-col flex-1 justify-between">
					<div>
						<p className="font-semibold">{order.cliente}</p>
						<p>{formatCnpjCpf(order.cnpjCpf)}</p>
						<p>{`${order.cidade} - ${order.estado}`}</p>
					</div>
					<div className="flex flex-1 justify-between mt-auto">
						<p
							className={cn(
								'text-sm font-semibold flex items-center gap-1',
								order?.valorTotal >= 0 ? '' : 'text-red-500'
							)}
						>
							{priceFormatter.format(order?.valorTotal)}
							{order?.valorTotal < 0 && (
								<Badge
									variant="outline"
									className="ml-1 px-1 py-0 h-5 bg-red-50 text-red-500 border-red-300 flex items-center"
								>
									<RefreshCcw className="h-3 w-3 mr-1" />
									<span className="text-[10px] text-nowrap">Devolução</span>
								</Badge>
							)}
						</p>
						<p>{dayjs(order?.dataEmissao).utc().format('DD/MM/YYYY')}</p>
					</div>
				</div>
			</CardContent>

			{!isTransmissionPage && (
				<div
					onKeyUp={(e) => {
						e.stopPropagation()
						handleViewClick(e as any)
					}}
					onClick={handleViewClick}
					className={cn(
						'absolute top-0 right-0 p-2 md:p-2 rounded-r-md h-full',
						isCurrentlySelected
							? 'border-primary bg-blue-500/5'
							: 'bg-gray-200/30',
					)}
				>
					<ExternalLink className="text-gray-600 ml-1" size={16} />
				</div>
			)}
		</Card>
	)
})
